import { useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Navigation as NavIcon, MapPin, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NavigationMap } from './components/NavigationMap';
import { InstructionPanel } from './components/InstructionPanel';
import { BottomControlPanel } from './components/BottomControlPanel';
import { StartPointPicker } from './components/StartPointPicker';
import { useNavigation } from './hooks/useNavigation';
import type { CostingModel } from '@/api/geo';

export function NavigationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Parse URL params
  const lat = parseFloat(searchParams.get('lat') || '0');
  const lon = parseFloat(searchParams.get('lon') || '0');
  const mode = (searchParams.get('mode') || 'auto') as CostingModel;

  // Validate params
  const isValidParams = lat !== 0 && lon !== 0 && !isNaN(lat) && !isNaN(lon);

  // State for manual start point
  const [showStartPicker, setShowStartPicker] = useState(false);
  const [manualStartPoint, setManualStartPoint] = useState<{ lat: number; lon: number } | null>(null);

  const { state, actions } = useNavigation({
    destination: { lat, lon },
    costing: mode,
    autoStart: isValidParams,
    manualStartPoint: manualStartPoint,
  });

  // Exit navigation - always go to home
  const handleExit = useCallback(() => {
    navigate('/', { replace: true });
    actions.stop();
  }, [actions, navigate]);

  const handleSelectManualStart = useCallback((point: { lat: number; lon: number }) => {
    setManualStartPoint(point);
    setShowStartPicker(false);
    // The useNavigation hook will detect the new manualStartPoint and fetch route
  }, []);

  const handleCancelStartPicker = useCallback(() => {
    setShowStartPicker(false);
  }, []);

  // Handle start point drag - reroute from new position
  const handleStartPointDrag = useCallback((point: { lat: number; lon: number }) => {
    actions.rerouteFromPoint(point);
  }, [actions]);

  // Show start point picker
  if (showStartPicker && isValidParams) {
    return (
      <StartPointPicker
        destination={{ lat, lon }}
        onSelectPoint={handleSelectManualStart}
        onCancel={handleCancelStartPicker}
      />
    );
  }

  // Invalid params error
  if (!isValidParams) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-6 bg-background">
        <AlertCircle className="w-16 h-16 text-destructive mb-4" />
        <h1 className="text-xl font-bold mb-2">Ошибка</h1>
        <p className="text-muted-foreground text-center mb-6">
          Некорректные координаты назначения
        </p>
        <Button onClick={handleExit}>
          <Home className="w-4 h-4 mr-2" />
          На главную
        </Button>
      </div>
    );
  }

  // Loading state
  if (state.isLoading && !state.route) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-6 bg-background">
        <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">
          {manualStartPoint ? 'Построение маршрута...' : 'Получение местоположения...'}
        </p>
      </div>
    );
  }

  // Error state - show option to pick start manually
  if (state.error && !state.route) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-6 bg-background">
        <AlertCircle className="w-16 h-16 text-amber-500 mb-4" />
        <h1 className="text-xl font-bold mb-2">Не удалось определить местоположение</h1>
        <p className="text-muted-foreground text-center mb-6">{state.error}</p>
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <Button onClick={() => setShowStartPicker(true)}>
            <MapPin className="w-4 h-4 mr-2" />
            Указать на карте
          </Button>
          <Button variant="outline" onClick={actions.start}>
            Попробовать снова
          </Button>
          <Button variant="ghost" onClick={handleExit}>
            <Home className="w-4 h-4 mr-2" />
            На главную
          </Button>
        </div>
      </div>
    );
  }

  // No route yet
  if (!state.route || state.maneuvers.length === 0) {
    return (
      <div className="h-screen flex flex-col items-center justify-center p-6 bg-background">
        <NavIcon className="w-16 h-16 text-primary mb-4" />
        <p className="text-muted-foreground">Построение маршрута...</p>
      </div>
    );
  }

  // Get current maneuver
  const currentManeuver = state.maneuvers[state.currentManeuverIndex];

  return (
    <div className="h-screen w-screen relative overflow-hidden">
      {/* Full-screen map */}
      <NavigationMap
        routeShape={state.decodedShape}
        userPosition={state.userPosition}
        startPoint={state.startPoint}
        destination={{ lat, lon }}
        followUser={state.followUser}
        onToggleFollow={actions.toggleFollow}
        onStartPointDrag={handleStartPointDrag}
      />

      {/* Top instruction panel */}
      <InstructionPanel
        instruction={currentManeuver?.instruction || ''}
        distanceToManeuver={state.distanceToNextManeuver}
        maneuverType={currentManeuver?.type || 0}
        onBack={handleExit}
        isOffRoute={state.isOffRoute}
      />

      {/* Bottom control panel */}
      <BottomControlPanel
        remainingDistance={state.remainingDistance}
        remainingTime={state.remainingTime}
        costing={state.costing}
        onCostingChange={actions.setCosting}
        onReroute={actions.reroute}
        onStop={handleExit}
        onToggleFollow={actions.toggleFollow}
        isRerouting={state.isRerouting}
        followUser={state.followUser}
      />
    </div>
  );
}
